/* Scratch-first flow, 2026-10-11: only successful native Shopify signup reveals and clips the code. */
/* Salty entry offer (sections/salty-welcome.liquid), 2026-09-16.
   Opens once for a first-time visitor after the section's delay (or on desktop exit intent), never for a signed-in
   customer, never on cart, account or password pages. Closing remembers the choice for 14 days; signing up remembers it
   for good. ?salty_popup=1 opens it anywhere, whatever the setting, for review.
   The card: the Salty packet is painted on a canvas and scratched away with a pointer; the 10% sits underneath. It opens
   when about 40% of the packet is scratched, or on Enter / Space for keyboard use. Coverage is tracked on a coarse grid,
   so the canvas is never read back.
   The form is a NORMAL page submit of Shopify's own customer form, on purpose. Measured 2026-09-21: Shopify's bot
   protection (hCaptcha) takes over the submit event and posts the form natively with its token, so a background fetch
   either double-posts or is rejected, and a check done in a submit handler never runs. So the check happens on the
   button CLICK, before any submit event exists; the page comes back with ?customer_posted=true#sw-form (thank-you
   state) or with the form's errors (the form reopens with the message, no second scratch).
   Email only since 2026-09-21 ("remove sms"): one field, one button, one post. */
(() => {
  if (customElements.get('salty-welcome')) return;
  const KEY = 'salty-welcome';
  const DAY = 864e5;
  const read = () => { try { return JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) { return null; } };
  const write = (v) => { try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (e) { /* private mode */ } };
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  class SaltyWelcome extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.dialog = this.querySelector('dialog');
      this.card = this.querySelector('[data-sw-card]');
      this.canvas = this.querySelector('canvas');
      this.form = this.querySelector('#sw-form');
      this.code = this.dataset.code;
      const params = new URLSearchParams(location.search);
      const forced = params.get('salty_popup') === '1';
      const back = location.hash === '#sw-form';
      const flag = this.form?.querySelector('[data-sw-posted]');
      const returned = back && (params.get('customer_posted') === 'true' || flag?.dataset.swPosted === 'true');
      const bounced = back && !returned ? (flag?.dataset.swErrors || '').trim() : '';

      this.querySelectorAll('[data-sw-close]').forEach((b) => b.addEventListener('click', () => this.close()));
      this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); });
      this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog && this.outside(e)) this.close(); });
      this.querySelectorAll('[data-sw-apply]').forEach((a) => a.addEventListener('click', async (e) => {
        e.preventDefault();
        if (!this.accepted) return;
        write({ state: 'used', at: Date.now() });
        const clipped = await this.clip();
        if (!clipped) { location.href = a.href; return; }          // the plain discount link still works without the clip
        if (location.pathname.startsWith('/products/')) this.dialog.close();   // already shopping: leave them where they are
        else location.href = this.dataset.shopPath;
      }));
      this.bindCard();
      this.bindForm();

      if (returned) {
        write({ state: 'joined', at: Date.now() });
        this.accepted = true;
        this.open('done');
        this.clip();
        return;
      }
      if (bounced) {
        try { this.querySelector('#sw-email').value = sessionStorage.getItem(KEY + '-email') || ''; } catch (e) { /* private mode */ }
        this.open('capture');
        this.fail(bounced, this.querySelector('#sw-email'));
        return;
      }
      if (forced) { this.open('offer'); return; }
      if (this.dataset.enabled !== 'true' || this.dataset.skip === 'true' || this.dataset.customer === 'true') return;
      if (/^\/(cart|checkout|account|challenge)/.test(location.pathname) || params.has('discount')) return;
      const seen = read();
      if (seen && (seen.state === 'joined' || seen.state === 'used' || Date.now() - seen.at < 14 * DAY)) return;

      const go = () => { cleanup(); this.openWhenFree(); };
      const timer = setTimeout(go, Math.max(2, +this.dataset.delay || 6) * 1000);
      const exit = (e) => { if (!e.relatedTarget && e.clientY <= 0) go(); };
      const cleanup = () => { clearTimeout(timer); document.documentElement.removeEventListener('mouseleave', exit); };
      if (matchMedia('(hover: hover) and (pointer: fine)').matches) document.documentElement.addEventListener('mouseleave', exit);
    }

    outside(e) {
      const r = this.dialog.getBoundingClientRect();
      return e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
    }

    // Wait for other open dialogs (cart drawer, menu) so the offer never stacks on top of a task.
    openWhenFree(tries = 0) {
      if (document.querySelector('dialog[open]') && tries < 20) { setTimeout(() => this.openWhenFree(tries + 1), 1500); return; }
      if (!document.querySelector('dialog[open]')) this.open('offer');
    }

    open(state) {
      this.setState(state);
      if (!this.dialog.open) this.dialog.showModal();
      // focus the dialog itself: a ring on the card at open reads as a second frame; Tab reaches close, then the card
      if (state === 'offer') { this.loadPacket(); this.paint(); this.dialog.focus({ preventScroll: true }); }
      else if (state === 'capture') { this.opened = true; this.card.classList.add('is-open'); this.card.setAttribute('aria-label', 'Revealed: 10% off your first order.'); this.querySelector('#sw-email')?.focus({ preventScroll: true }); }
      else if (state === 'done') { this.opened = true; this.card.classList.add('is-open'); this.card.setAttribute('aria-label', `Revealed: 10% off your first order. Your code is ${this.code}.`); this.querySelector('[data-sw-show="done"] a')?.focus({ preventScroll: true }); }
    }

    close() {
      const s = read();
      if (!s || (s.state !== 'joined' && s.state !== 'used')) write({ state: 'closed', at: Date.now() });
      this.dialog.close();
    }

    setState(state) {
      this.dialog.dataset.state = state;
      this.querySelectorAll('[data-sw-show]').forEach((el) => { el.hidden = !el.dataset.swShow.split(' ').includes(state); });
    }

    /* ---- the scratch card ---- */
    bindCard() {
      this.cols = 10; this.rows = 12;
      this.hit = new Set();
      let down = false, last = null;
      const scratchAt = (e) => {
        const r = this.canvas.getBoundingClientRect();
        const x = e.clientX - r.left, y = e.clientY - r.top;
        const ctx = this.ctx;
        ctx.globalCompositeOperation = 'destination-out';
        ctx.lineCap = 'round';
        ctx.lineWidth = Math.max(34, r.width * 0.18) * this.dpr;
        ctx.beginPath();
        if (last) { ctx.moveTo(last.x * this.dpr, last.y * this.dpr); ctx.lineTo(x * this.dpr, y * this.dpr); }
        else { ctx.arc(x * this.dpr, y * this.dpr, ctx.lineWidth / 2, 0, Math.PI * 2); }
        last ? ctx.stroke() : ctx.fill();
        // mark every grid cell the stroke passed through
        const steps = last ? Math.ceil(Math.hypot(x - last.x, y - last.y) / 6) : 1;
        for (let i = 0; i <= steps; i++) {
          const px = last ? last.x + (x - last.x) * (i / steps) : x;
          const py = last ? last.y + (y - last.y) * (i / steps) : y;
          const rad = ctx.lineWidth / this.dpr / 2;
          for (let dx = -rad; dx <= rad; dx += r.width / this.cols) {
            for (let dy = -rad; dy <= rad; dy += r.height / this.rows) {
              const cx = Math.floor((px + dx) / r.width * this.cols), cy = Math.floor((py + dy) / r.height * this.rows);
              if (cx >= 0 && cy >= 0 && cx < this.cols && cy < this.rows) this.hit.add(cy * this.cols + cx);
            }
          }
        }
        last = { x, y };
        if (this.hit.size / (this.cols * this.rows) >= 0.4) this.reveal();
      };
      this.card.addEventListener('pointerdown', (e) => {
        if (this.opened) return;
        down = true; last = null;
        this.card.setPointerCapture(e.pointerId);
        scratchAt(e);
      });
      this.card.addEventListener('pointermove', (e) => { if (down && !this.opened) scratchAt(e); });
      const up = () => { down = false; last = null; };
      this.card.addEventListener('pointerup', up);
      this.card.addEventListener('pointercancel', up);
      // keyboard and switch access: Enter / Space open it (a pointer click never does, so taps scratch instead)
      this.card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.reveal(); }
      });
      this.card.addEventListener('click', (e) => { if (e.detail === 0) this.reveal(); });
    }

    // The packet picture is 545 KB. It is fetched when the offer opens, never on a page view where it stays shut
    // (measured 2026-09-21: it used to download on every page, for every visitor, with the pop-up switched off).
    loadPacket() {
      if (this.img) return;
      this.img = new Image();
      this.img.decoding = 'async';
      this.img.addEventListener('load', () => { if (this.dialog.open && !this.opened) this.paint(); });
      this.img.src = this.dataset.packet;
    }

    paint() {
      if (this.opened) return;
      const r = this.card.getBoundingClientRect();
      if (!r.width) return;
      this.dpr = Math.min(2, window.devicePixelRatio || 1);
      this.canvas.width = Math.round(r.width * this.dpr);
      this.canvas.height = Math.round(r.height * this.dpr);
      const ctx = this.ctx = this.canvas.getContext('2d');
      const w = this.canvas.width, h = this.canvas.height;
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = '#F4F4EB';
      ctx.fillRect(0, 0, w, h);
      if (this.img && this.img.complete && this.img.naturalWidth) {
        // the packet fills the card height, centered, like the pouch sitting in the frame
        const s = Math.max(w / this.img.naturalWidth, (h * 1.02) / this.img.naturalHeight);
        const iw = this.img.naturalWidth * s, ih = this.img.naturalHeight * s;
        ctx.drawImage(this.img, (w - iw) / 2, (h - ih) / 2 + h * 0.02, iw, ih);
      } else {
        ctx.fillStyle = '#F15A24';
        ctx.fillRect(w * 0.16, h * 0.1, w * 0.68, h * 0.8);
      }
      this.hit.clear();
    }

    reveal() {
      if (this.opened) return;
      this.opened = true;
      this.card.classList.add('is-open');
      this.card.setAttribute('aria-label', 'Revealed: 10% off your first order.');
      if (!reduced() && navigator.vibrate) { try { navigator.vibrate(18); } catch (e) { /* not allowed */ } }
      this.setState('capture');
      this.querySelector('#sw-email')?.focus({ preventScroll: true });
    }

    /* ---- the coupon ----
       Shopify's /discount/CODE link puts the code on the visitor's session (cookie discount_code), so it shows up in the
       cart and at checkout with nothing to type. Measured 2026-09-21: fetched in the background it sets the cookie, the
       visitor stays on the page, and a $32 cart reads $28.80. redirect=/cart.js keeps the response tiny. */
    clip() {
      if (!this.accepted) return Promise.resolve(false);
      if (!this.clipping) {
        this.clipping = fetch(this.dataset.clipUrl, { credentials: 'same-origin' })
          .then((r) => {
            if (!r.ok) return false;
            this.querySelectorAll('[data-sw-clipped]').forEach((el) => { el.hidden = false; });
            this.querySelectorAll('[data-sw-unclipped]').forEach((el) => { el.hidden = true; });
            return true;
          })
          .catch(() => false);
      }
      return this.clipping;
    }

    /* ---- the form ---- */
    fail(msg, field) {
      const error = this.form.querySelector('[data-sw-error]');
      error.textContent = msg; error.hidden = false;
      if (field) { field.setAttribute('aria-invalid', 'true'); field.focus(); }
    }

    bindForm() {
      if (!this.form) return;
      const email = this.form.querySelector('#sw-email');
      const error = this.form.querySelector('[data-sw-error]');
      const submit = this.form.querySelector('[data-sw-submit]');
      email.addEventListener('input', () => { email.removeAttribute('aria-invalid'); error.hidden = true; });

      // True = the form may leave.
      const ready = () => {
        error.hidden = true;
        const mail = email.value.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(mail)) { this.fail('Enter an email like name@example.com.', email); return false; }
        email.value = mail;
        try { sessionStorage.setItem(KEY + '-email', mail); } catch (e) { /* private mode */ }
        return true;
      };

      // Enter in the field clicks this button too, so one listener covers every way of sending the form.
      // Capture phase + stopImmediatePropagation: a rejected click never becomes a submit event for anyone
      // (Shopify's bot check, when it is on, listens for submit and would otherwise post the form as it stands).
      submit.addEventListener('click', (e) => {
        if (!ready()) { e.preventDefault(); e.stopImmediatePropagation(); return; }
        submit.setAttribute('aria-busy', 'true');
      }, true);
      // Belt and braces for a submit that did not come from the button (a script, a password manager).
      this.form.addEventListener('submit', (e) => { if (!ready()) { e.preventDefault(); e.stopImmediatePropagation(); } }, true);
    }
  }
  customElements.define('salty-welcome', SaltyWelcome);
})();
